import React, { createContext, useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { translations } from '../translations';
import { hotelTranslations, tourTranslations } from '../contentTranslations';

const LanguageContext = createContext();

export const pagePaths = {
  home: { da: '/da', en: '/en' },
  destinations: { da: '/da/handicapvenlige-ferie-destinationer', en: '/en/accessible-holiday-destinations' },
  hotels: { da: '/da/handicapvenlige-hoteller', en: '/en/accessible-hotels' },
  'featured-trips': { da: '/da/udvalgte-rejser', en: '/en/selected-trips' },
  about: { da: '/da/om-mig', en: '/en/about-me' },
  prices: { da: '/da/priser', en: '/en/prices' },
  contact: { da: '/da/kontakt', en: '/en/contact' },
  terms: { da: '/da/rejsebetingelser', en: '/en/terms-and-conditions' },
  gdpr: { da: '/da/gdpr', en: '/en/gdpr' },
  consent: { da: '/da/samtykke', en: '/en/consent' },
  quote: { da: '/da/tilbud', en: '/en/request-quote' },
  traveltips: { da: '/da/rejsetips', en: '/en/travel-tips' },
  'traveltips-guide': { da: '/da/rejsetips/hvad-skal-du-huske-paa-ferie-som-koerestolsbruger', en: '/en/travel-tips/what-to-remember-on-holiday-as-a-wheelchair-user' },
  gallery: { da: '/da/galleri-oversigt', en: '/en/gallery' },
  practical: { da: '/da/praktisk-information', en: '/en/practical-information' },
  destination: { da: '/da/destination/:id', en: '/en/destination/:id' },
  hotel: { da: '/da/hotel/:hotelId', en: '/en/hotel/:hotelId' },
  'hotel-tour': { da: '/da/hotel/:hotelId/turforslag', en: '/en/hotel/:hotelId/tour-suggestions' },
  'destination-tour': { da: '/da/destination/:id/turforslag', en: '/en/destination/:id/tour-suggestions' }
};

// Map of old paths to redirect to new paths
export const redirects = {
  '/': '/da',
  '/destinationer': '/da/handicapvenlige-ferie-destinationer',
  '/hoteller': '/da/handicapvenlige-hoteller',
  '/handicapvenlige-ferie-destinationer': '/da/handicapvenlige-ferie-destinationer',
  '/handicapvenlige-hoteller': '/da/handicapvenlige-hoteller',
  '/udvalgte-rejser': '/da/udvalgte-rejser',
  '/om-mig': '/da/om-mig',
  '/priser': '/da/priser',
  '/kontakt': '/da/kontakt',
  '/rejsebetingelser': '/da/rejsebetingelser',
  '/gdpr': '/da/gdpr',
  '/samtykke': '/da/samtykke',
  '/tilbud': '/da/tilbud',
  '/rejsetips': '/da/rejsetips',
  '/rejsetips/hvad-skal-du-huske-paa-ferie-som-koerestolsbruger': '/da/rejsetips/hvad-skal-du-huske-paa-ferie-som-koerestolsbruger',
  '/galleri-oversigt': '/da/galleri-oversigt',
  '/praktisk-information': '/da/praktisk-information',
  // English old paths redirects
  '/en/destinations': '/en/accessible-holiday-destinations',
  '/en/hotels': '/en/accessible-hotels',
  '/en/selected-trips': '/en/selected-trips',
  '/en/featured-trips': '/en/selected-trips',
  '/en/about': '/en/about-me',
  '/en/terms': '/en/terms-and-conditions',
  '/en/quote': '/en/request-quote',
  '/en/tips': '/en/travel-tips',
  '/en/gallery-overview': '/en/gallery',
  '/en/practical': '/en/practical-information'
};

export const findPageKeyAndParams = (pathname) => {
  for (const [key, langs] of Object.entries(pagePaths)) {
    for (const [lang, pathPattern] of Object.entries(langs)) {
      const regexPattern = '^' + pathPattern
        .replace(/:[a-zA-Z0-9]+/g, '([^/]+)')
        .replace(/\//g, '\\/') + '$';
      const regex = new RegExp(regexPattern);
      const match = pathname.match(regex);
      if (match) {
        const paramNames = (pathPattern.match(/:[a-zA-Z0-9]+/g) || []).map(p => p.slice(1));
        const params = {};
        paramNames.forEach((name, index) => {
          params[name] = match[index + 1];
        });
        return { key, lang, params };
      }
    }
  }
  return null;
};

export const getLocalizedPath = (pageKey, lang, params = {}) => {
  const pathPattern = pagePaths[pageKey]?.[lang];
  if (!pathPattern) return '/da';
  
  let path = pathPattern;
  Object.entries(params).forEach(([key, value]) => {
    path = path.replace(`:${key}`, value);
  });
  return path;
};

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Determine current language from URL
  const isEnglish = location.pathname.startsWith('/en') || location.pathname.startsWith('/en/');
  const language = isEnglish ? 'en' : 'da';

  // Handle Redirects
  useEffect(() => {
    const pathname = location.pathname;
    
    // Check direct redirects map
    if (redirects[pathname]) {
      navigate(redirects[pathname], { replace: true });
      return;
    }

    // Check dynamic old paths (without /da or /en prefix)
    if (!pathname.startsWith('/da') && !pathname.startsWith('/en')) {
      // e.g. /destination/mallorca -> /da/destination/mallorca
      if (pathname.startsWith('/destination/')) {
        navigate(`/da${pathname}`, { replace: true });
        return;
      }
      if (pathname.startsWith('/hotel/')) {
        navigate(`/da${pathname}`, { replace: true });
        return;
      }
    }
  }, [location.pathname, navigate]);

  const t = (key) => {
    return translations[language]?.[key] || translations['da']?.[key] || key;
  };

  const changeLanguage = (newLang) => {
    if (newLang === language) return;

    const currentInfo = findPageKeyAndParams(location.pathname);
    if (currentInfo) {
      const newPath = getLocalizedPath(currentInfo.key, newLang, currentInfo.params);
      navigate(newPath);
    } else {
      // Fallback to home of the selected language
      navigate(newLang === 'en' ? '/en' : '/da');
    }
  };

  const translateObject = (obj) => {
    if (!obj) return obj;
    if (language === 'da') return obj;

    let translated = { ...obj };
    
    // Merge hotel translations if applicable
    if (obj.id && hotelTranslations[obj.id]) {
      translated = { ...translated, ...hotelTranslations[obj.id] };
    }
    
    // Merge tour area translations if applicable
    if (obj.id && tourTranslations[obj.id]) {
      translated = { ...translated, ...tourTranslations[obj.id] };
    }
    
    Object.keys(obj).forEach(key => {
      if (key.endsWith('_en')) {
        const baseKey = key.slice(0, -3);
        if (obj[key] !== undefined) {
          translated[baseKey] = obj[key];
        }
      }
    });

    // Handle specific nested structures
    if (obj.tourSuggestions_en) {
      translated.tourSuggestions = obj.tourSuggestions_en;
    }
    if (obj.tours_en) {
      translated.tours = obj.tours_en;
    }
    if (obj.features_en) {
      translated.features = obj.features_en;
    }
    if (obj.amenities_en) {
      translated.amenities = obj.amenities_en;
    }
    if (obj.distances_en) {
      translated.distances = obj.distances_en;
    }
    if (obj.seasonalPrices_en) {
      translated.seasonalPrices = obj.seasonalPrices_en;
    }
    if (obj.aboutText_en) {
      translated.aboutText = obj.aboutText_en;
    }

    return translated;
  };

  return (
    <LanguageContext.Provider value={{ language, t, changeLanguage, getLocalizedPath, translateObject }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
